// Service de tracking des événements utilisateur
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

class TrackingService {
  constructor() {
    this.sessionId = this.getOrCreateSessionId();
    this.queue = [];
    this.isSending = false;
    this.batchInterval = 5000; // Envoyer les événements toutes les 5 secondes
    
    // Démarrer le batch sender
    this.startBatchSender();
    
    // Envoyer les événements restants avant fermeture
    window.addEventListener('beforeunload', () => this.flushQueue());
  }

  getOrCreateSessionId() {
    let sessionId = sessionStorage.getItem('tracking_session_id');
    if (!sessionId) {
      sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('tracking_session_id', sessionId);
    }
    return sessionId;
  }

  // Tracker un événement
  track(eventType, targetType, targetId = null, metadata = {}) {
    const event = {
      event_type: eventType,
      target_type: targetType,
      target_id: targetId,
      metadata: {
        ...metadata,
        url: window.location.pathname,
        timestamp: new Date().toISOString()
      },
      session_id: this.sessionId
    };

    this.queue.push(event);

    // Si la queue est grande, envoyer immédiatement
    if (this.queue.length >= 10) {
      this.flushQueue();
    }
  }

  // Événements prédéfinis
  trackPageView(pageName, metadata = {}) {
    this.track('view', 'page', pageName, metadata);
  }

  trackSpotView(spotId, spotTitle = null) {
    this.track('view', 'spot', spotId, { title: spotTitle });
  }

  trackSpotClick(spotId, spotTitle = null, source = 'list') {
    this.track('click', 'spot', spotId, { title: spotTitle, source });
  }

  trackSpotLike(spotId, isLiked) {
    this.track('like', 'spot', spotId, { action: isLiked ? 'like' : 'unlike' });
  }

  trackSpotComment(spotId) {
    this.track('comment', 'spot', spotId);
  }

  trackSpotShare(spotId, platform = 'unknown') {
    this.track('share', 'spot', spotId, { platform });
  }

  trackSearch(query, resultsCount = 0) {
    this.track('search', 'search', null, { query, results_count: resultsCount });
  }

  trackFilter(filterType, filterValue) {
    this.track('filter', 'filter', null, { filter_type: filterType, value: filterValue, tag: filterValue });
  }

  trackMapInteraction(action, coordinates = null) {
    this.track('click', 'map', null, { action, coordinates });
  }

  trackUserAction(action, targetId = null, metadata = {}) {
    this.track(action, 'user', targetId, metadata);
  }

  // Envoyer la queue d'événements
  async flushQueue() {
    if (this.isSending || this.queue.length === 0) return;

    this.isSending = true;
    const eventsToSend = [...this.queue];
    this.queue = [];

    try {
      if (eventsToSend.length === 1) {
        // Envoyer un seul événement
        await fetch(`${BACKEND_URL}/api/track/event`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(eventsToSend[0])
        });
      } else {
        // Envoyer un batch
        await fetch(`${BACKEND_URL}/api/track/batch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(eventsToSend)
        });
      }
    } catch (error) {
      // En cas d'erreur, remettre les événements dans la queue
      console.warn('Failed to send tracking events:', error);
      this.queue = [...eventsToSend, ...this.queue];
    } finally {
      this.isSending = false;
    }
  }

  startBatchSender() {
    setInterval(() => this.flushQueue(), this.batchInterval);
  }
}

// Instance singleton
const trackingService = new TrackingService();

export default trackingService;

// Exports nommés pour plus de facilité
export const track = (eventType, targetType, targetId, metadata) => 
  trackingService.track(eventType, targetType, targetId, metadata);

export const trackSpotView = (spotId, spotTitle) => 
  trackingService.trackSpotView(spotId, spotTitle);

export const trackSpotClick = (spotId, spotTitle, source) => 
  trackingService.trackSpotClick(spotId, spotTitle, source);

export const trackSpotLike = (spotId, isLiked) => 
  trackingService.trackSpotLike(spotId, isLiked);

export const trackSpotComment = (spotId) => 
  trackingService.trackSpotComment(spotId);

export const trackSpotShare = (spotId, platform) => 
  trackingService.trackSpotShare(spotId, platform);

export const trackSearch = (query, resultsCount) => 
  trackingService.trackSearch(query, resultsCount);

export const trackFilter = (filterType, filterValue) => 
  trackingService.trackFilter(filterType, filterValue);

export const trackMapInteraction = (action, coordinates) => 
  trackingService.trackMapInteraction(action, coordinates);

export const trackPageView = (pageName, metadata) => 
  trackingService.trackPageView(pageName, metadata);
